// services/phoneAuthService.js
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from './firebaseConfig';

class PhoneAuthService {
  constructor() {
    this.recaptchaVerifier = null;
    this.confirmationResult = null;
  }

  initRecaptcha(containerId, invisible = true) {
    try {
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
      }

      this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: invisible ? 'invisible' : 'normal',
        callback: (response) => {
          console.log('Recaptcha verified');
        },
        'expired-callback': () => {
          console.log('Recaptcha expired');
        },
      });

      return this.recaptchaVerifier;
    } catch (error) {
      console.error('Error initializing recaptcha:', error);
      throw this.handleError(error);
    }
  }

  async sendOTP(phoneNumber) {
    try {
      if (!this.recaptchaVerifier) {
        throw new Error('Recaptcha not initialized. Call initRecaptcha first.');
      }

      this.confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        this.recaptchaVerifier
      );

      return {
        success: true,
        message: 'OTP sent successfully',
        confirmationResult: this.confirmationResult,
      };
    } catch (error) {
      console.error('Error sending OTP:', error);
      throw this.handleError(error);
    }
  }

  async verifyOTP(confirmationResult, code) {
    try {
      if (!confirmationResult) {
        throw new Error('No confirmation result. Please request OTP first.');
      }

      const result = await confirmationResult.confirm(code);

      return {
        success: true,
        message: 'Phone number verified successfully',
        user: result.user,
        phoneNumber: result.user.phoneNumber,
      };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw this.handleError(error);
    }
  }

  handleError(error) {
    const errorCode = error.code;
    const errorMessage = error.message;

    let userMessage = 'An error occurred. Please try again.';

    switch (errorCode) {
      case 'auth/invalid-phone-number':
        userMessage = 'Please enter a valid phone number';
        break;
      case 'auth/too-many-requests':
        userMessage = 'Too many attempts. Please try again after 1 hour';
        break;
      case 'auth/invalid-verification-code':
        userMessage = 'Invalid code. Please try again';
        break;
      case 'auth/code-expired':
        userMessage = 'Code expired. Please click to resend';
        break;
      case 'auth/network-request-failed':
        userMessage = 'Network error. Please check your connection';
        break;
      case 'auth/quota-exceeded':
        userMessage = 'Service temporarily unavailable. Please try later';
        break;
      case 'auth/captcha-check-failed':
        userMessage = 'Captcha verification failed. Please try again';
        break;
      default:
        userMessage = errorMessage || 'An unexpected error occurred';
    }

    return {
      code: errorCode,
      message: userMessage,
      originalError: error,
    };
  }

  cleanup() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
    this.confirmationResult = null;
  }

  reset() {
    this.cleanup();
  }
}

export default new PhoneAuthService();
